/**
 * Vasaloppet Mina Sidor
 * Author: Peter Löfås, peter@lofas.se
 */

import { Box, Button, Card, CardContent, CardHeader, DialogContent, Dialog, DialogActions, DialogTitle, Grid, IconButton, LinearProgress, Tab, Tabs, Theme, Typography, createStyles, withStyles } from '@material-ui/core';
import { ArrowForward, Edit, OpenInBrowser } from '@material-ui/icons';
import GetAppIcon from '@material-ui/icons/GetApp';
import UploadIcon from '@material-ui/icons/CloudUpload';
import RefreshIcon from '@material-ui/icons/Refresh';
import { debounce } from 'lodash';
import React, { Fragment } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { B2bCompany } from '../../../model/B2bCompany';
import { ListEntry } from '../../../model/Entry';
import { IExportExcelRequest } from '../../../model/IExportExcelRequest';
import { Order } from '../../../model/Order';
import FirebaseContext from '../../../providers/Firebase/context';
import { ApiBackend } from '../../../providers/apibackend';
import SearchFilter, { Filters } from '../../Common/SearchFilter/SearchFilter';
import { searchFilterToExportExcelFilter } from '../../Common/SearchFilter/utils';
import withConfirm, { IConfirmContext } from '../../Common/dialog/Confirm';
import EditEntryDialog from './EditEntryDialog';
import { vasaloppetDateTimeGetterFormatter } from '../../../utilities/date';
import { ListEntriesParameters } from '../../../providers/models';
import { IColumnDefinition, ISortModel, PageSize } from '../../Common/EnhancedTable/models';
import EnhancedTable from '../../Common/EnhancedTable/EnhancedTable';
import { EntryStatus } from '../../../model/CommonTypes';
import ImportEntriesForm from '../Import/ImportEntries';
import TabPanel from '../../Common/TabPanel';

interface ISearchFilter {
    lastName?: string;
    firstName?: string;
    productVariant?: string[];
    bib?: string;
    startgroup?: string[];
    vasaId?: string;
    birthDate?: string;
    club?: string;
    email?: string;
    phone?: string;
    status?: EntryStatus;
    company?: string;
    code?: string;
    nationality?: string;
    raceStatus?: string;
    prePackingGroup?: string;
    team?: string;
    eventId?: string;
    onlyRelayMembers?: boolean;
}

interface ITabState {
    entries: ListEntry[];
    page: number;
    pageSize: number;
    rowCount: number;
    sortModel: ISortModel<ListEntry>;
}

interface State {
    loading: boolean;
    companies: B2bCompany[];
    searchFilter: ISearchFilter;
    deleteConfirmation: Order;
    showDeleteConfirmation: boolean;
    editEntry: ListEntry;
    activeTab: number;
    entriesState: ITabState;
    relayEntriesState: ITabState;
    importDialogOpen: boolean;
}

type editEntryMode = "dialog" | "in-app" | "new-tab";

type IProps = RouteComponentProps & IConfirmContext;
type SearchFilterType = Omit<ListEntriesParameters, "noCount" | "sortProp" | "sortDir" | "begin" | "limit">;

class ManageEntries extends React.Component<IProps, State> {
    static contextType = FirebaseContext;
    state: State;

    private readonly api: ApiBackend;

    constructor(props: IProps) {
        super(props);

        this.state = {
            editEntry: null,
            loading: true,
            companies: [],
            deleteConfirmation: null,
            showDeleteConfirmation: false,
            searchFilter: {},
            entriesState: {
                entries: null,
                page: 0,
                pageSize: 10,
                rowCount: 0,
                sortModel: null
            },
            relayEntriesState: {
                entries: null,
                page: 0,
                pageSize: 10,
                rowCount: 0,
                sortModel: null
            },
            activeTab: 0,
            importDialogOpen: false
        };
        this.api = new ApiBackend();
    }

    async componentDidMount(): Promise<void> {
        await this.init();
    }

    refresh = () => {
        this.setState({ loading: true });
        this.performSearch();
    }

    editEntry = (entry: ListEntry, mode: editEntryMode): void => {
        switch (mode) {
            case "dialog":
                this.setState({ editEntry: entry });
                break;
            case "in-app":
                this.props.history.push(`manage/${entry.id}`);
                break;
            case "new-tab":
                window.open(`manage/${entry.id}`);
                break;
        }
    };

    render() {
        const { classes } = this.props as any;
        const { companies, activeTab, entriesState, relayEntriesState } = this.state;

        const companyValueGetter = (row: ListEntry) => {
            const company = companies?.find(x => x.id == row.b2bId);
            return company?.name;
        };

        const columnDefinitions: IColumnDefinition<ListEntry>[] = [
            {
                renderCell: (row) => {
                    return <>
                        <IconButton color="default" onClick={() => { this.editEntry(row, "dialog") }} size="small">
                            <Edit fontSize="medium" />
                        </IconButton>
                        <IconButton color="default" onClick={() => { this.editEntry(row, "new-tab") }} size="small">
                            <OpenInBrowser fontSize="medium" />
                        </IconButton>
                        <IconButton color="default" onClick={() => { this.editEntry(row, "in-app") }} size="small">
                            <ArrowForward fontSize="medium" />
                        </IconButton>
                    </>
                }
            },
            { propName: "lastName", label: "Efternamn" },
            { propName: "firstName", label: "Förnamn" },
            { propName: "eventName", label: "Lopp" },
            { propName: "bibtext", label: "Startnummer" },
            { propName: "startgroupName", label: "Startled" },
            { propName: "vasaId", label: "VASA-ID" },
            { propName: "birthDay", label: "Födelsedatum" },
            { propName: "itemPrice", label: "Pris" },
            { propName: "clubName", label: "Klubb" },
            { propName: "email", label: "E-post" },
            { propName: "phone", label: "Telefon" },
            {
                propName: "orderDate",
                label: "Orderdatum",
                valueFormatter: (row) => vasaloppetDateTimeGetterFormatter(row.orderDate, "---"),
                sortValueGetter: (row) => vasaloppetDateTimeGetterFormatter(row.orderDate, "---")
            },
            { propName: "status", label: "Status" },
            {
                propName: "b2bId",
                label: "Företag",
                valueFormatter: (row) => companyValueGetter(row),
                sortValueGetter: (row) => companyValueGetter(row)
            },
            { propName: "raceStatus", label: "Racestatus" },
            { propName: "prePackingGroup", label: "Packningsgrupp" },
            { propName: "fisCode", label: "FIS-Kod" },
            { propName: "team", label: "Lag" },
            { propName: "leg", label: "Sträcka" }
        ];

        const defaultBoxStyle = {
            display: "flex",
            flexDirection: "row",
        };

        return <>
            <SearchFilter<SearchFilterType>
                id={"manage-entries-filter-2"}
                filters={{
                    "lastName": {
                        id: "filter-last-name",
                        type: "DebouncedText",
                        label: "Efternamn",
                        size: 4
                    },
                    "firstName": {
                        id: "filter-first-name",
                        type: "DebouncedText",
                        label: "Förnamn",
                        size: 4
                    },
                    "eventId": {
                        id: "filter-event-period",
                        type: "EventId",
                        label: "Evenemang",
                        useWildCardSelect: true,
                        size: 4
                    },
                    "bib": {
                        id: "filter-bib",
                        type: "DebouncedText",
                        label: "Startnummer",
                        size: 4
                    },
                    "startgroup": {
                        id: "filter-startgroup",
                        type: "StartGroup",
                        label: "Startled",
                        size: 4
                    },
                    "productVariant": {
                        id: "filter-productVariant",
                        type: "ProductList",
                        label: "Lopp",
                        size: 4,
                        productTypes: ["race", "event"],
                        useVariants: true,
                        multiple: true
                    },
                    "vasaId": {
                        id: "filter-vasaId",
                        type: "DebouncedText",
                        label: "VASA-ID",
                        size: 4
                    },
                    "birthDate": {
                        id: "filter-birthDate",
                        type: "BirthDate",
                        label: "Födelsedatum",
                        size: 4
                    },
                    "club": {
                        id: "filter-club",
                        type: "DebouncedText",
                        label: "Klubb",
                        size: 4
                    },
                    "email": {
                        id: "filter-email",
                        type: "DebouncedText",
                        label: "E-post",
                        size: 4
                    },
                    "phone": {
                        id: "filter-phone",
                        type: "DebouncedText",
                        label: "Telefon",
                        size: 4
                    },
                    "status": {
                        id: "filter-status",
                        type: "EntryStatus",
                        label: "Status",
                        size: 4
                    },
                    "company": {
                        id: "filter-company",
                        type: "Company",
                        label: "Företag",
                        size: 4,
                        includeArchived: true
                    },
                    "code": {
                        id: "filter-code",
                        type: "DebouncedText",
                        label: "Kod",
                        size: 4
                    },
                    "nationality": {
                        id: "filter-nationality",
                        type: "CountryPicker",
                        label: "Nationalitet",
                        size: 4
                    },
                    "raceStatus": {
                        id: "filter-race-status",
                        type: "RaceStatus",
                        label: "Racestatus",
                        size: 4
                    },
                    "prePackingGroup": {
                        id: "filter-pre-packing-group",
                        type: "DebouncedText",
                        label: "Packningsgrupp",
                        size: 4
                    },
                    "team": {
                        id: "filter-team",
                        type: "DebouncedText",
                        label: "Lag",
                        size: 4
                    }
                }}
                persist={true}
                onInit={async (filter: Filters<SearchFilterType>) => {
                    await this.handleSearchFilterChange(filter as unknown as ISearchFilter);
                }}
                onChange={async (filter: Filters<SearchFilterType>) => {
                    await this.handleSearchFilterChange(filter as unknown as ISearchFilter);
                }}
            />
            <Grid container className={classes.root} spacing={2}>
                <Grid item xs={12}>
                    <Tabs
                        value={activeTab}
                        className={classes.tabs}
                        onChange={this.handleTabChange}
                    >
                        <Tab label={`Anmälningar (${entriesState.rowCount})`} />
                        <Tab label={`Stafettdeltagare (${relayEntriesState.rowCount})`} />
                    </Tabs>
                    <TabPanel value={activeTab} index={0}>
                        <Card>
                            <CardHeader className={classes.cardHeader}
                                title={<>
                                    <Typography variant="h5" style={{ display: "inline" }}>Anmälningar</Typography>
                                    <IconButton onClick={this.refresh}>
                                        <RefreshIcon color="primary" />
                                    </IconButton>
                                </>}
                                action={
                                    <Box {...defaultBoxStyle} style={{ marginTop: 6 }}>
                                        <IconButton
                                            onClick={this.import}>
                                            <UploadIcon color="primary" />
                                        </IconButton>
                                        <IconButton
                                            onClick={this.export(0)}
                                        >
                                            <GetAppIcon color="primary" />
                                        </IconButton>
                                    </Box>
                                }
                            />
                            <CardContent>
                                {this.state.loading &&
                                    <LinearProgress color="secondary" />
                                }
                                <EnhancedTable<ListEntry>
                                    columnDefinitions={columnDefinitions}
                                    data={entriesState.entries ?? []}
                                    pageSize={entriesState.pageSize as PageSize}
                                    page={entriesState.page}
                                    rowCount={entriesState.rowCount}
                                    paginationMode="server"
                                    sortingMode="server"
                                    loading={this.state.loading}
                                    dense
                                    onRequestPageChange={this.setPage(0)}
                                    onRequestSort={this.setSortModel(0)}
                                />
                            </CardContent>
                        </Card>
                    </TabPanel>
                    <TabPanel value={activeTab} index={1}>
                        <Card>
                            <CardHeader className={classes.cardHeader}
                                title={<>
                                    <Typography variant="h5" style={{ display: "inline" }}>Anmälningar</Typography>
                                    <IconButton onClick={this.refresh}>
                                        <RefreshIcon color="primary" />
                                    </IconButton>
                                </>}
                                action={
                                    <Box {...defaultBoxStyle} style={{ marginTop: 6 }}>
                                        <IconButton
                                            onClick={this.export(1)}
                                        >
                                            <GetAppIcon color="primary" />
                                        </IconButton>
                                    </Box>
                                }
                            />
                            <CardContent>
                                {this.state.loading &&
                                    <LinearProgress color="secondary" />
                                }
                                <EnhancedTable<ListEntry>
                                    columnDefinitions={columnDefinitions}
                                    data={relayEntriesState.entries ?? []}
                                    pageSize={relayEntriesState.pageSize as PageSize}
                                    page={relayEntriesState.page}
                                    rowCount={relayEntriesState.rowCount}
                                    paginationMode="server"
                                    sortingMode="server"
                                    loading={this.state.loading}
                                    dense
                                    onRequestPageChange={this.setPage(1)}
                                    onRequestSort={this.setSortModel(1)}
                                />
                            </CardContent>
                        </Card>
                    </TabPanel>
                </Grid>
            </Grid>
            {this.state && this.state.showDeleteConfirmation && this.renderConfirmationDialog()}
            {this.state && this.state.importDialogOpen && this.renderImportDialog()}
            {this.state && this.state.editEntry &&
                <EditEntryDialog
                    entryId={this.state.editEntry.id}
                    onAbortEdit={() => {
                        this.setState({ editEntry: null });
                        this.refresh();
                    }}
                />
            }
        </>;
    }

    renderImportDialog() {
        return <Dialog
            disableEscapeKeyDown
            maxWidth="lg"
            aria-labelledby="confirmation-dialog-title"
            open={true}
        >
            <DialogTitle id="confirmation-dialog-title">Importera anmälningar</DialogTitle>
            <DialogContent>
                <ImportEntriesForm close={() => {
                    this.setState({ importDialogOpen: false });
                }} />
            </DialogContent>
            <DialogActions>
                <Button variant="contained" autoFocus onClick={() => {
                    this.setState({ importDialogOpen: false });
                }} color="primary">
                    Avbryt
                </Button>
            </DialogActions>
        </Dialog>;
    }

    renderConfirmationDialog() {
        return <Dialog
            disableEscapeKeyDown
            maxWidth="xs"
            aria-labelledby="confirmation-dialog-title"
            open={true}
        >
            <DialogTitle id="confirmation-dialog-title">Är du säker på att du vill ta bort ordern?</DialogTitle>
            <DialogActions>
                <Button variant="contained" autoFocus onClick={this.handleDeleteCancel} color="primary">
                    Avbryt
                </Button>
                <Button variant="contained" onClick={this.handleDeleteOk} color="primary">
                    Ja, ta bort
                </Button>
            </DialogActions>
        </Dialog>;
    }

    handleDeleteCancel = () => {
        this.setState({ deleteConfirmation: null, showDeleteConfirmation: false });
    }

    handleDeleteOk = () => {
        this.setState({ loading: true });
    }

    private handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        this.setState({ activeTab: newValue });
    };
    private import = async () => {
        this.setState({ importDialogOpen: true });
    }
    private export = (tab: 0 | 1) => async () => {
        const { searchFilter, entriesState, relayEntriesState } = this.state;

        const getSearchfilter = (): ISearchFilter => {
            if (tab === 0) {
                return searchFilter;
            }

            return {
                ...searchFilter,
                onlyRelayMembers: true
            }
        };
        let sortModel: ISortModel<ListEntry>;
        if (tab === 0) {
            sortModel = entriesState.sortModel;
        } else {
            sortModel = relayEntriesState.sortModel;
        }

        const filter = searchFilterToExportExcelFilter<ISearchFilter>(getSearchfilter());

        if (!filter || filter.length === 0) {
            const result = await this.props.showConfirm("Du håller på att exportera data utan något filter, vill du fortsätta?");
            if (!result) {
                return;
            }
        }

        const request: IExportExcelRequest = {
            entityType: "Entry",
            filter: filter,
            sortProp: sortModel?.sortBy,
            sortDir: sortModel?.sortOrder
        };
        try {
            this.setState({ loading: true });
            await this.api.exportExcel(request);
        } finally {
            this.setState({ loading: false });
        }
    };

    private handleSearchFilterChange = async (searchFilter: ISearchFilter): Promise<void> => {
        const { entriesState, relayEntriesState } = this.state;

        const nextEntriesState = {
            ...entriesState,
            page: 0
        };
        const nextRelayEntriesState = {
            ...relayEntriesState,
            page: 0
        }
        this.setState({ searchFilter: searchFilter, entriesState: nextEntriesState, relayEntriesState: nextRelayEntriesState, loading: true }, async () => {
            await this.performSearch();
        });
    };

    private setPage = (tab: 0 | 1) => (nextPage: number, pageSize?: number): void => {
        const { entriesState, relayEntriesState } = this.state;

        let nextState: Partial<State> = {
            loading: true
        };

        if (tab === 0) {
            nextState.entriesState = {
                ...entriesState,
                page: nextPage,
                pageSize: pageSize
            };
        } else {
            nextState.relayEntriesState = {
                ...relayEntriesState,
                page: nextPage,
                pageSize: pageSize
            };
        }

        this.setState(nextState as State, () => {
            this.performSearch();
        });
    };

    private setSortModel = (tab: 0 | 1) => (nextSortModel: ISortModel<ListEntry>): void => {
        const { entriesState, relayEntriesState } = this.state;

        let nextState: Partial<State> = {
            loading: true
        };

        if (tab === 0) {
            nextState.entriesState = {
                ...entriesState,
                sortModel: nextSortModel
            };
        } else {
            nextState.relayEntriesState = {
                ...relayEntriesState,
                sortModel: nextSortModel
            };
        }

        this.setState(nextState as State, () => {
            this.performSearch();
        });
    };

    private init = async (): Promise<void> => {
        const companies = await this.api.listB2bCompanies({ includeArchived: true });
        this.setState({ companies: companies });
    };

    private performSearch = debounce(async () => {
        const { searchFilter, entriesState, relayEntriesState } = this.state;

        const getParams = (tab: 0 | 1): ListEntriesParameters => {
            if (tab === 0) {
                return {
                    ...searchFilter,
                    sortProp: entriesState.sortModel?.sortBy as string,
                    sortDir: entriesState.sortModel?.sortOrder,
                    begin: entriesState.page * entriesState.pageSize,
                    limit: entriesState.pageSize
                };
            }

            const relaySearchFilter = {
                ...searchFilter,
                onlyRelayMembers: true
            }

            return {
                ...relaySearchFilter,
                sortProp: relayEntriesState.sortModel?.sortBy as string,
                sortDir: relayEntriesState.sortModel?.sortOrder,
                begin: relayEntriesState.page * relayEntriesState.pageSize,
                limit: relayEntriesState.pageSize
            };
        };

        const work = [
            this.api.listEntries(getParams(0)),
            this.api.listEntries(getParams(1))
        ];

        const res = await Promise.all(work);
        const nextEntriesState = {
            ...entriesState,
            entries: res[0]?.entries,
            rowCount: res[0]?.count ?? 0
        };

        const nextRelayEntriesState = {
            ...relayEntriesState,
            entries: res[1]?.entries,
            rowCount: res[1]?.count ?? 0
        };

        this.setState({
            entriesState: nextEntriesState,
            relayEntriesState: nextRelayEntriesState,
            loading: false
        });
    }, 500);
}

const useStyles = ({ palette, spacing }: Theme) => createStyles({
    cardHeader: {
        background: palette.secondary.main,
        color: palette.secondary.contrastText,
        padding: 3
    },
    photo: {
        height: '30px',
        verticalAlign: 'middle',
        borderRadius: '10px'
    },
    root: {

    },
    form: {
        '& > *': {
            margin: spacing(1),
            width: '25ch',
        },
        '& label.Mui-focused': {
            color: palette.secondary.main,
        },
        '& .MuiInput-underline:after': {
            borderBottomColor: palette.secondary.main,
        },
    }
});

export default withRouter(withConfirm(withStyles(useStyles)(ManageEntries)));
